import React, { useState, useEffect } from 'react';
import { ordersApi, statsApi, shippingApi, slaApi, inventoryApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatTime } from '../utils/dateUtils';
import { DocumentTextIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { InventoryAlertModal } from '../components/InventoryAlertModal';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    todayOrders: 0,
    awaitingShipment: 0,
    shippedToday: 0,
    slaBreached: 0,
    avgShipTimeFormatted: '--',
    shippedCount30d: 0,
  });
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [slaBreachedOrders, setSlaBreachedOrders] = useState<any[]>([]);
  const [showSlaModal, setShowSlaModal] = useState(false);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingSla, setLoadingSla] = useState(false);
  const [slaSettings, setSlaSettings] = useState<{
    threshold: number;
    useBusinessHours: boolean;
  }>({ threshold: 24, useBusinessHours: false });
  
  // Manifest state
  const [showManifestModal, setShowManifestModal] = useState(false);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [manifestResult, setManifestResult] = useState<{
    success: boolean;
    download_url?: string;
    shipment_count?: number;
    skipped_already_manifested?: number;
    range?: { label?: string };
    orders?: Array<{ order_number: string; tracking_number: string; service: string }>;
    error?: string;
  } | null>(null);
  const todayStr = new Date().toISOString().slice(0, 10);
  const [manifestMode, setManifestMode] = useState<'today' | 'range'>('today');
  const [manifestStartDate, setManifestStartDate] = useState<string>(todayStr);
  const [manifestEndDate, setManifestEndDate] = useState<string>(todayStr);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load stats and SLA settings in parallel
      const [statsData, slaData, ordersData, inventoryData] = await Promise.all([
        statsApi.getStats(),
        slaApi.getSettings(),
        ordersApi.list({ status: 'awaiting_shipment', limit: 5 }),
        inventoryApi.getLowStockProducts().catch(() => ({ count: 0 })),
      ]);

      setStats({
        todayOrders: statsData.today_orders,
        awaitingShipment: statsData.awaiting_shipment,
        shippedToday: statsData.shipped_today,
        slaBreached: statsData.sla_breached,
        avgShipTimeFormatted: statsData.avg_ship_time_formatted || '--',
        shippedCount30d: statsData.shipped_count_30d || 0,
      });

      setSlaSettings({
        threshold: slaData.shipping_threshold_hours || 24,
        useBusinessHours: slaData.use_business_hours || false,
      });

      setRecentOrders(ordersData.orders || []);
      setLowStockCount(inventoryData.count || 0);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSlaClick = async () => {
    if (stats.slaBreached === 0) return;
    
    setLoadingSla(true);
    try {
      const data = await statsApi.getSlaBreachedOrders();
      // SLA endpoint returns breached_orders, not orders
      setSlaBreachedOrders(data.breached_orders || data.orders || []);
      setShowSlaModal(true);
    } catch (error) {
      console.error('Failed to load SLA breached orders:', error);
    } finally {
      setLoadingSla(false);
    }
  };

  const handleCreateManifest = async () => {
    setManifestLoading(true);
    setManifestResult(null);
    try {
      const params =
        manifestMode === 'range'
          ? { startDate: manifestStartDate, endDate: manifestEndDate }
          : { date: todayStr };
      const result = await shippingApi.createManifest(params);
      setManifestResult({
        success: true,
        download_url: result.download_url,
        shipment_count: result.shipment_count,
        skipped_already_manifested: result.skipped_already_manifested,
        range: result.range,
        orders: result.orders,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to create manifest';
      setManifestResult({
        success: false,
        error: errorMessage,
      });
    } finally {
      setManifestLoading(false);
    }
  };

  const handlePrintManifest = () => {
    if (manifestResult?.download_url) {
      window.open(manifestResult.download_url, '_blank');
    }
  };

  const formatHoursWaiting = (hours: number) => {
    if (hours < 24) return `${Math.round(hours)} hours`;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);
    return days === 1 ? `${days} day, ${remainingHours} hrs` : `${days} days, ${remainingHours} hrs`;
  };

  const formatOrderNumber = (orderNumber: string) => {
    if (!orderNumber) return '';
    // If it already has a prefix (PSCS or starts with #), return as-is
    if (orderNumber.startsWith('PSCS') || orderNumber.startsWith('#')) return orderNumber;
    // If it's a Faire order (contains letters), add # prefix
    if (/[A-Za-z]/.test(orderNumber)) return `#${orderNumber}`;
    // Otherwise add PSCS prefix for regular orders
    return `PSCS${orderNumber}`;
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div 
          className={`bg-white p-6 rounded-xl border shadow-subtle ${
            stats.slaBreached > 0 
              ? 'border-red-300 bg-red-50 cursor-pointer hover:bg-red-100 transition-colors' 
              : 'border-slate-200'
          }`}
          onClick={handleSlaClick}
        >
          <p className="text-sm font-medium text-slate-600">SLA Breached</p>
          <p className={`text-3xl font-bold mt-2 ${stats.slaBreached > 0 ? 'text-red-600' : 'text-slate-900'}`}>
            {loading ? '-' : stats.slaBreached}
          </p>
          <p className={`text-sm mt-2 ${stats.slaBreached > 0 ? 'text-red-600' : 'text-slate-500'}`}>
            {stats.slaBreached > 0 
              ? 'Click to view orders' 
              : `Orders > ${slaSettings.threshold} ${slaSettings.useBusinessHours ? 'business ' : ''}hours`}
          </p>
        </div>

        <div 
          className={`bg-white p-6 rounded-xl border shadow-subtle ${
            lowStockCount > 0 
              ? 'border-orange-300 bg-orange-50 cursor-pointer hover:bg-orange-100 transition-colors' 
              : 'border-slate-200'
          }`}
          onClick={() => lowStockCount > 0 && setShowInventoryModal(true)}
        >
          <p className="text-sm font-medium text-slate-600">Low Inventory</p>
          <p className={`text-3xl font-bold mt-2 ${lowStockCount > 0 ? 'text-orange-600' : 'text-slate-900'}`}>
            {loading ? '-' : lowStockCount}
          </p>
          <p className={`text-sm mt-2 ${lowStockCount > 0 ? 'text-orange-600' : 'text-slate-500'}`}>
            {lowStockCount > 0 ? 'Click to view products' : 'All stocked up'}
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-subtle">
          <p className="text-sm font-medium text-slate-600">Awaiting Shipment</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">
            {loading ? '-' : stats.awaitingShipment}
          </p>
          <p className="text-sm text-slate-500 mt-2">Ready to ship</p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-subtle">
          <p className="text-sm font-medium text-slate-600">Shipped Today</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">
            {loading ? '-' : stats.shippedToday}
          </p>
          <p className="text-sm text-slate-500 mt-2">Labels created today</p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-subtle">
          <p className="text-sm font-medium text-slate-600">Avg Ship Time</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">
            {loading ? '-' : stats.avgShipTimeFormatted}
          </p>
          <p className="text-sm text-slate-500 mt-2">
            {stats.shippedCount30d > 0 ? `${stats.shippedCount30d} orders · 30 days` : 'Business hours'}
          </p>
        </div>
      </div>

      {/* USPS SCAN Form Section */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-subtle mb-8">
        <div className="px-6 py-4 border-b border-slate-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <DocumentTextIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">USPS End of Day</h2>
              <p className="text-sm text-slate-500">Generate SCAN form for driver pickup</p>
            </div>
          </div>
          <button
            onClick={() => setShowManifestModal(true)}
            className="w-full sm:w-auto px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-blue-hover transition-colors flex items-center justify-center gap-2 tap-target"
          >
            <PrinterIcon className="w-4 h-4" />
            Create SCAN Form
          </button>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-slate-600">
            The USPS SCAN form lets drivers scan a single barcode instead of every package. Generate it at the end of your shipping day — or, if USPS skipped a pickup, you can combine multiple days into one form using the date range option.
          </p>
        </div>
      </div>

      {/* Manifest Modal */}
      {showManifestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                USPS SCAN Form
              </h2>
              <button
                onClick={() => {
                  setShowManifestModal(false);
                  setManifestResult(null);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {!manifestResult ? (
                <div>
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <DocumentTextIcon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2 text-center">Create End of Day Manifest</h3>
                  <p className="text-sm text-slate-600 mb-4 text-center">
                    Generate a SCAN form so the driver can scan one barcode instead of every package.
                    Already-manifested shipments are skipped automatically.
                  </p>

                  <div className="inline-flex w-full rounded-lg border border-slate-200 p-1 mb-4 bg-slate-50">
                    <button
                      type="button"
                      onClick={() => setManifestMode('today')}
                      className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                        manifestMode === 'today'
                          ? 'bg-white shadow-sm text-slate-900 font-medium'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={() => setManifestMode('range')}
                      className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                        manifestMode === 'range'
                          ? 'bg-white shadow-sm text-slate-900 font-medium'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Date range
                    </button>
                  </div>

                  {manifestMode === 'today' ? (
                    <p className="text-sm text-slate-500 mb-6 text-center">
                      Including USPS shipments from {new Date().toLocaleDateString()}.
                    </p>
                  ) : (
                    <div className="mb-6 grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">From</label>
                        <input
                          type="date"
                          value={manifestStartDate}
                          max={manifestEndDate}
                          onChange={(e) => setManifestStartDate(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">To</label>
                        <input
                          type="date"
                          value={manifestEndDate}
                          min={manifestStartDate}
                          max={todayStr}
                          onChange={(e) => setManifestEndDate(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                        />
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleCreateManifest}
                    disabled={manifestLoading}
                    className="w-full px-4 py-3 bg-brand-blue text-white rounded-lg hover:bg-brand-blue-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {manifestLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating SCAN Form...
                      </>
                    ) : (
                      <>
                        <DocumentTextIcon className="w-5 h-5" />
                        Generate SCAN Form
                      </>
                    )}
                  </button>
                </div>
              ) : manifestResult.success ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">SCAN Form Created!</h3>
                  <p className="text-sm text-slate-600 mb-1">
                    Generated for {manifestResult.shipment_count} USPS shipment(s)
                    {manifestResult.range?.label ? ` from ${manifestResult.range.label}` : ''}
                  </p>
                  {manifestResult.skipped_already_manifested && manifestResult.skipped_already_manifested > 0 ? (
                    <p className="text-xs text-slate-500 mb-4">
                      {manifestResult.skipped_already_manifested} shipment(s) skipped (already in a prior manifest)
                    </p>
                  ) : (
                    <p className="mb-4" />
                  )}
                  
                  {manifestResult.orders && manifestResult.orders.length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-4 mb-4 max-h-48 overflow-y-auto text-left">
                      <p className="text-xs font-medium text-slate-500 mb-2">Included Orders:</p>
                      <div className="space-y-1">
                        {manifestResult.orders.map((order, idx) => (
                          <div key={idx} className="text-xs text-slate-600 flex justify-between">
                            <span>{order.order_number}</span>
                            <span className="text-slate-400">{order.tracking_number}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handlePrintManifest}
                    className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <PrinterIcon className="w-5 h-5" />
                    Print / Download SCAN Form
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Could Not Create SCAN Form</h3>
                  <p className="text-sm text-red-600 mb-6">
                    {manifestResult.error}
                  </p>
                  <button
                    onClick={() => setManifestResult(null)}
                    className="w-full px-4 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SLA Breached Modal */}
      {showSlaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                SLA Breached Orders ({slaBreachedOrders.length})
              </h2>
              <button
                onClick={() => setShowSlaModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingSla ? (
                <div className="text-center py-8 text-slate-600">Loading...</div>
              ) : slaBreachedOrders.length === 0 ? (
                <div className="text-center py-8 text-slate-600">No SLA breached orders</div>
              ) : (
                <div className="space-y-3">
                  {slaBreachedOrders.map((order) => (
                    <div
                      key={order.id}
                      onClick={() => {
                        setShowSlaModal(false);
                        navigate(`/orders?selected=${order.id}`);
                      }}
                      className="p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{formatOrderNumber(order.order_number)}</p>
                          <p className="text-sm text-slate-600">
                            {order.first_name} {order.last_name} • {order.shipping_city}, {order.shipping_state}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Ordered: {formatDate(order.shopify_created_at)} {formatTime(order.shopify_created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-600">
                            {formatHoursWaiting(parseFloat(order.hours_waiting))}
                          </p>
                          <p className="text-sm text-slate-600">
                            ${parseFloat(order.total_price).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => {
                  setShowSlaModal(false);
                  navigate('/orders');
                }}
                className="w-full px-5 py-3 text-base font-semibold bg-brand-blue text-white rounded-lg hover:bg-brand-blue-hover transition-colors shadow-md"
              >
                View All Orders
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Alert Modal */}
      <InventoryAlertModal
        isOpen={showInventoryModal}
        onClose={() => {
          setShowInventoryModal(false);
          loadDashboardData();
        }}
      />

      {/* Recent Orders */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Recent Orders</h2>
        </div>
        <div className="divide-y divide-slate-200">
          {loading ? (
            <div className="px-6 py-12 text-center text-slate-600">
              Loading recent orders...
            </div>
          ) : recentOrders.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-600">
              No orders yet
            </div>
          ) : (
            recentOrders.map((order) => (
              <div
                key={order.id}
                onClick={() => navigate('/orders')}
                className="px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {formatOrderNumber(order.order_number)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {order.first_name} {order.last_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                      Awaiting Shipment
                    </span>
                    <span className="text-sm font-medium text-slate-900">
                      ${order.total_price ? parseFloat(order.total_price.toString()).toFixed(2) : '0.00'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
